// Pure decoder: (opCode, bytes) -> typed MatchEvent | null.
//
// A faithful port of `lib/core/network/match_event_decoder.dart`. That file's
// warning applies here verbatim and is the reason this is a port and not a
// reimplementation: every rule mirrors a handler in the running Flutter
// client, and an "improvement" here is a silent divergence between two clients
// talking to the same authoritative server.
//
// TWO RULES THAT LOOK LIKE SLOPPINESS AND ARE NOT:
//
//  1. An unknown opCode returns null, quietly. That is what lets the server
//     ship a new event before this renderer handles it. It is also why opcode
//     drift is dangerous enough to need a codegen guard — a mismatched number
//     does not throw, it just stops being handled. See
//     tool/gen_opcodes_ts.dart in the parent repo.
//
//  2. Every field is read defensively, with a default. A payload from a server
//     that has moved on must degrade, not throw: an exception in the socket's
//     message handler takes down delivery of EVERY subsequent event, not just
//     the one that failed.

import { OpCode } from './opcodes.generated';
import type {
  MatchEndReward,
  MatchEvent,
  MatchStateSnapshot,
  PresenceSummary,
  RankingEntry,
  RoundResults,
  RoundResultsPair,
} from './messages';

/** Decodes one match-data frame. Returns null for anything not handled. */
export function decodeMatchData(opCode: number, data: Uint8Array | null): MatchEvent | null {
  try {
    return decode(opCode, data);
  } catch (err) {
    // Never rethrow into the socket's listener — see rule 2 above.
    console.warn('[decode] dropped a malformed frame', { opCode, err });
    return null;
  }
}

function decode(opCode: number, data: Uint8Array | null): MatchEvent | null {
  switch (opCode) {
    case OpCode.newUserJoined:
      return { kind: 'newUserJoined', user: presence(json(data)) };

    case OpCode.joinedMatch:
      return { kind: 'joinedMatch', state: matchState(json(data)) };

    case OpCode.selectAlphabetRequest:
      return { kind: 'selectAlphabetRequest', currentRound: int(json(data), 'currentRound', 0) };

    case OpCode.newRoundBegin: {
      const m = json(data);
      return {
        kind: 'newRoundBegin',
        alphabet: str(m, 'alphabet', ''),
        answersDeadline: intOrNull(m, 'answersDeadline'),
      };
    }

    case OpCode.allowGameStart:
      return { kind: 'allowGameStart' };

    // Server -> joiner: rejected because the match is full, locked, or already
    // started. The server sends an empty payload for all three reasons alike
    // and does not distinguish them, so neither does this.
    case OpCode.matchFull:
      return { kind: 'matchFull' };

    case OpCode.stop:
      return { kind: 'stopRound' };

    case OpCode.roundResults:
      return { kind: 'roundResults', data: roundResults(json(data)) };

    case OpCode.matchEnd:
      // An EMPTY payload is legitimate: a legacy server, or the idempotent
      // replay path once rewards are already granted. It decodes to all
      // zeros, and `rank === 0` means NO SIGNAL — never a loss.
      return { kind: 'matchEnd', reward: matchEnd(data && data.length > 0 ? json(data) : {}) };

    case OpCode.playerDisconnected:
      return { kind: 'playerDisconnected', user: presence(json(data)) };

    case OpCode.playerReconnected:
      return { kind: 'playerReconnected', user: presence(json(data)) };

    case OpCode.kickedOutTimedOut:
      return { kind: 'playerKickedOut', userId: str(json(data), 'userId', '') };

    case OpCode.userToSelectNextAlphabet: {
      const m = json(data);
      return {
        kind: 'userToSelectNextAlphabet',
        userId: str(m, 'userId', ''),
        username: str(m, 'username', ''),
      };
    }

    case OpCode.systemSelectAlphabet: {
      const m = json(data);
      return {
        kind: 'systemSelectAlphabet',
        userId: str(m, 'userId', ''),
        username: str(m, 'username', ''),
      };
    }

    case OpCode.updateHost: {
      const m = json(data);
      const newHost = obj(m['newHost']);
      const oldHost = obj(m['oldHost']);
      return {
        kind: 'hostUpdate',
        newHostUserId: str(newHost, 'userId', ''),
        // Null-safe, unlike the Dart handler's null-bang on a conditional
        // path — the decoder serves ALL callers, not only the self-host branch.
        oldHostUsername: oldHost['username'] === undefined ? null : str(oldHost, 'username', ''),
      };
    }

    case OpCode.acknowledgeAnswersReceived:
      return { kind: 'answersAcknowledged', round: int(json(data), 'round', 0) };

    // The next-round deadline MOVED (the fast-advance path, once every online
    // human has acknowledged results). The deadline is server-authoritative,
    // so a change to it is published like any other state rather than
    // inferred. A missing or wrong-typed value becomes null, which the
    // countdown reads as "no countdown" and hides.
    case OpCode.nextRoundDeadlineUpdate: {
      const m = json(data);
      return {
        kind: 'nextRoundDeadline',
        deadlineMs: intOrNull(m, 'nextRoundDeadline'),
        isFinalRound: m['isFinalRound'] === true,
      };
    }

    // Client-only sends (4, 6, 8, 11, 13, 16, 17), the deliberate no-op
    // hostQuit (14), the notification-borne classroom codes (24, 25), and
    // anything this build predates.
    default:
      return null;
  }
}

// --- field readers ----------------------------------------------------------

type Json = Record<string, unknown>;

function json(data: Uint8Array | null): Json {
  if (!data || data.length === 0) return {};
  return obj(JSON.parse(new TextDecoder().decode(data)));
}

function obj(v: unknown): Json {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Json) : {};
}

function str(m: Json, key: string, fallback: string): string {
  const v = m[key];
  return typeof v === 'string' ? v : fallback;
}

function int(m: Json, key: string, fallback: number): number {
  const v = m[key];
  return typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : fallback;
}

function intOrNull(m: Json, key: string): number | null {
  const v = m[key];
  return typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : null;
}

function bool(m: Json, key: string): boolean {
  return m[key] === true;
}

function strArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((e): e is string => typeof e === 'string') : [];
}

function presence(m: Json): PresenceSummary {
  return {
    userId: str(m, 'userId', ''),
    username: str(m, 'username', ''),
    // "01" and "US" mirror the Flutter client's fallbacks for an unresolvable
    // player, so a stranger in the ranking renders the same in both clients.
    avatar: str(m, 'avatar', '01'),
    country: str(m, 'country', 'US'),
  };
}

function matchState(m: Json): MatchStateSnapshot {
  const roster: Record<string, PresenceSummary> = {};
  for (const [userId, raw] of Object.entries(obj(m['presencesSummary']))) {
    roster[userId] = presence(obj(raw));
  }

  const picker = m['userToSelectAlphabet'];

  return {
    alias: str(m, 'alias', ''),
    categories: strArray(m['categories']),
    rounds: int(m, 'rounds', 0),
    difficulty: int(m, 'difficulty', 0),
    host: str(m, 'host', ''),
    presencesSummary: roster,
    rejoining: bool(m, 'rejoining'),
    gameStarted: bool(m, 'gameStarted'),
    currentRound: int(m, 'currentRound', 0),
    currentAlphabet: m['currentAlphabet'] === null ? null : str(m, 'currentAlphabet', '') || null,
    isScoring: bool(m, 'isScoring'),
    currentRoundScored: bool(m, 'currentRoundScored'),
    answersDeadline: intOrNull(m, 'answersDeadline'),
    isSelectingAlphabet: bool(m, 'isSelectingAlphabet'),
    userToSelectAlphabet: picker ? presence(obj(picker)) : null,
    selectedAlphabets: strArray(m['selectedAlphabets']),
    answersSubmitted: m['answersSubmitted'] ? obj(m['answersSubmitted']) : null,
    scores: m['scores'] ? obj(m['scores']) : null,
  };
}

function roundResults(m: Json): RoundResults {
  const results: Record<string, Record<string, RoundResultsPair>> = {};
  for (const [userId, byCategory] of Object.entries(obj(m['results']))) {
    const row: Record<string, RoundResultsPair> = {};
    for (const [category, pair] of Object.entries(obj(byCategory))) {
      const p = obj(pair);
      row[category] = { answer: str(p, 'answer', ''), score: int(p, 'score', 0) };
    }
    results[userId] = row;
  }

  const rankingRaw = m['ranking'];
  const ranking: RankingEntry[] = Array.isArray(rankingRaw)
    ? rankingRaw.map((raw) => {
        const e = obj(raw);
        return {
          userId: str(e, 'userId', ''),
          score: int(e, 'score', 0),
          ranking: int(e, 'ranking', 0),
        };
      })
    : [];

  return {
    results,
    ranking,
    nextRoundDeadline: intOrNull(m, 'nextRoundDeadline'),
    isFinalRound: m['isFinalRound'] === true,
  };
}

function matchEnd(m: Json): MatchEndReward {
  return {
    coins: int(m, 'coins', 0),
    xp: int(m, 'xp', 0),
    rank: int(m, 'rank', 0),
    newCoins: intOrNull(m, 'newCoins'),
    sig: m['sig'] === undefined ? null : str(m, 'sig', '') || null,
    issuedAt: intOrNull(m, 'issuedAt'),
    // intOrNull, not int(…, 0): an ABSENT score (a server predating the
    // partner leaderboard) must stay distinguishable from a score the server
    // deliberately set to zero because the match earned no ranked credit.
    score: intOrNull(m, 'score'),
  };
}
